import { MIN_ATTACK_COOLDOWN, DASH_COOLDOWN, BLOCK_COOLDOWN, PLAYER_SIZE, POTION_COOLDOWN } from '../../constants';
import { WEAPON_ATTACK_PROFILES }          from '../../data/items';
// ── Atoms ──────────────────────────────────────────────────────────────────────
import { HpBar }              from '../atoms/HpBar';
import { XpBar }              from '../atoms/XpBar';
import { FloatingText }       from '../atoms/FloatingText';
import { ActiveModifierBadge } from '../atoms/ActiveModifierBadge';
import { TierBossWarning }    from '../atoms/TierBossWarning';
// ── Molecules ─────────────────────────────────────────────────────────────────
import { LevelUpOverlay }       from '../molecules/LevelUpOverlay';
import { SpellHotbar }          from '../molecules/SpellHotbar';
import { TierAnnouncement }     from '../molecules/TierAnnouncement';
import { PauseOverlay }         from '../molecules/PauseOverlay';
import { MagicSelectOverlay }   from '../molecules/MagicSelectOverlay';
import { WorldModifierOverlay } from '../molecules/WorldModifierOverlay';
import { ChestLootModal }       from '../molecules/ChestLootModal';
import { AttackSweep }          from '../molecules/AttackSweep';
import { EnvironmentAnnouncement } from '../molecules/EnvironmentAnnouncement';
import { SpellEffectsLayer }    from '../molecules/SpellEffectsLayer';
import { PlayerEntity }         from '../molecules/PlayerEntity';
import { PondLayer }            from '../molecules/PondLayer';
import { TerrainLayer }         from '../molecules/TerrainLayer';
import { ChestLayer }           from '../molecules/ChestLayer';
import { GroundDropLayer }      from '../molecules/GroundDropLayer';
import { ProjectileLayer }      from '../molecules/ProjectileLayer';
import { EnemyLayer }           from '../molecules/EnemyLayer';
import { AbilityBar }           from '../molecules/AbilityBar';
import { StatsHUD }             from '../molecules/StatsHUD';

// ── Ground theme data ─────────────────────────────────────────────────────────

// Black tint opacity added over the env ground colour per modifier rarity.
// common = no effect; legendary = 80% black overlay.
const MODIFIER_TINT = {
  common:    0,
  uncommon:  0.20,
  rare:      0.40,
  epic:      0.60,
  legendary: 0.80,
};

// Base background colour + scrolling texture per environment
const ENV_GROUND = {
  forest: {
    bg: '#2d5a1b', border: '#1a3d0e',
    // Grass blades: near-vertical fine lines at two angles
    texture: [
      'repeating-linear-gradient(176deg, transparent 0px, transparent 10px, rgba(0,30,0,0.22) 10px, rgba(0,30,0,0.22) 11px)',
      'repeating-linear-gradient(184deg, transparent 0px, transparent 16px, rgba(60,120,0,0.10) 16px, rgba(60,120,0,0.10) 17px)',
    ].join(','),
    tileH: 16,
  },
  desert: {
    bg: '#c8a456', border: '#9a7830',
    // Sand ripples: gentle diagonal dune lines
    texture: [
      'repeating-linear-gradient(158deg, transparent 0px, transparent 20px, rgba(220,170,60,0.16) 20px, rgba(220,170,60,0.16) 22px)',
      'repeating-linear-gradient(162deg, transparent 0px, transparent 34px, rgba(180,130,40,0.10) 34px, rgba(180,130,40,0.10) 36px)',
    ].join(','),
    tileH: 36,
  },
  snow: {
    bg: '#e8f0f5', border: '#b0c4d0',
    // Snow surface: subtle grid of faint white lines
    texture: [
      'repeating-linear-gradient(180deg, rgba(255,255,255,0.12) 0px, rgba(255,255,255,0.12) 1px, transparent 1px, transparent 18px)',
      'repeating-linear-gradient(90deg,  rgba(255,255,255,0.07) 0px, rgba(255,255,255,0.07) 1px, transparent 1px, transparent 26px)',
    ].join(','),
    tileH: 18,
  },
  castle: {
    bg: '#8a8a9a', border: '#606070',
    // Grey brick pattern: horizontal mortar rows + staggered vertical joints
    texture: [
      'repeating-linear-gradient(180deg, transparent 0px, transparent 18px, rgba(0,0,0,0.40) 18px, rgba(0,0,0,0.40) 20px)',
      'repeating-linear-gradient(90deg,  transparent 0px, transparent 38px, rgba(0,0,0,0.28) 38px, rgba(0,0,0,0.28) 40px)',
    ].join(','),
    tileH: 20,
  },
  water: {
    bg: '#1a5a9a', border: '#0f3d6e',
    // Waves: pairs of semi-transparent horizontal ripple bands
    texture: [
      'repeating-linear-gradient(180deg, transparent 0px, transparent 7px, rgba(40,160,240,0.18) 7px, rgba(40,160,240,0.18) 9px, transparent 9px, transparent 20px, rgba(20,110,200,0.10) 20px, rgba(20,110,200,0.10) 22px)',
    ].join(','),
    tileH: 22,
  },
  graveyard: {
    bg: '#3a3a4a', border: '#252535',
    // Dark sparse grass: dim near-vertical lines, wider spaced
    texture: [
      'repeating-linear-gradient(177deg, transparent 0px, transparent 14px, rgba(0,0,0,0.30) 14px, rgba(0,0,0,0.30) 15px)',
      'repeating-linear-gradient(90deg,  transparent 0px, transparent 22px, rgba(80,120,60,0.07) 22px, rgba(80,120,60,0.07) 23px)',
    ].join(','),
    tileH: 15,
  },
  inferno: {
    bg: '#1a1a1a', border: '#0f0f0f',
    // Very dark sand with faint ember-red ripples
    texture: [
      'repeating-linear-gradient(158deg, transparent 0px, transparent 18px, rgba(200,50,0,0.18) 18px, rgba(200,50,0,0.18) 20px)',
      'repeating-linear-gradient(180deg, transparent 0px, transparent 28px, rgba(255,80,0,0.07) 28px, rgba(255,80,0,0.07) 29px)',
    ].join(','),
    tileH: 28,
  },
};

/**
 * The game canvas: renders all entities, HUD elements, overlays.
 * Receives the raw mutable game-state snapshot (s) so renders stay in sync
 * with the rAF loop without unnecessary copies.
 *
 * Also rendered while gameState === 'paused' (frozen world + PauseOverlay).
 */
export function GameWorld({
  s, gameState, attackRenderKey, levelUpSkills, isInvincible,
  onSkillChoose, onResume, onMenu, onSettings,
  magicSelectSpells, magicSelectIsUpgrade, onMagicSpellChoose,
  worldModifierChoices, onModifierChoose,
  chestLootOptions, onChestLootChoose,
  envAnnouncement,
  gameWorldRef,
  onMobileAttack, onMobileDash, onMobileCast, onMobilePause, onMobileBlock, onMobilePotion,
}) {
  const attackProfile = WEAPON_ATTACK_PROFILES[s.weaponType ?? 'unarmed'] ?? WEAPON_ATTACK_PROFILES.unarmed;
  const now = performance.now();

  // Spell state flags for visuals
  const isShielded = now < (s.shieldUntil   ?? 0);
  const isFrozen   = now < (s.frozenUntil   ?? 0);
  const isSlowTime = now < (s.slowTimeUntil ?? 0);
  const isPaused   = gameState === 'paused';
  // Dodge blur: show for 200 ms after a successful dodge
  const isDodging  = now < (s.lastDodgeTime ?? -99999) + 200;

  // ── Ability cooldown progress (0 = full overlay, 1 = ready) ──────────────
  const effectiveCd  = Math.max(MIN_ATTACK_COOLDOWN, attackProfile.baseCd * (s.cdMult ?? 1) * (s.activeModifier?.playerCdMult ?? 1));
  const attackCdProg = Math.min(1, (now - (s.lastAttackTime ?? -effectiveCd))  / effectiveCd);
  const dashCdProg   = Math.min(1, (now - (s.lastDashTime  ?? -DASH_COOLDOWN)) / DASH_COOLDOWN);
  const effectiveBlockCd = Math.max(100, BLOCK_COOLDOWN - (s.blockCooldownReduction ?? 0));
  const blockCdProg   = Math.min(1, (now - (s.lastBlockTime  ?? -effectiveBlockCd)) / effectiveBlockCd);
  const potionCdProg  = Math.min(1, (now - (s.lastPotionTime ?? -POTION_COOLDOWN))  / POTION_COOLDOWN);

  // ── Ground theme — env base colour + optional modifier darkness overlay ──
  const env         = s.environment ?? 'forest';
  const envTheme    = ENV_GROUND[env] ?? ENV_GROUND.forest;
  // Texture scrolls in sync with terrain movement
  const bgScrollY   = (s.bgScrollTotal ?? 0) % (envTheme.tileH ?? 20);
  // Darkness tint: 0 (common/none) → 0.80 (legendary)
  const tintOpacity = MODIFIER_TINT[s.activeModifier?.rarity] ?? 0;
  // When the modifier-selection screen is active, shift the ground itself to
  // the same dark-red hue the old opaque overlay used (#1c0606 ≈ rgb(28,6,6)).
  // The existing transition smoothly interpolates from the env colour.
  const groundBg    = gameState === 'modifier' ? '#1c0606' : envTheme.bg;

  return (
    <div
      ref={gameWorldRef}
      className="game-world"
      style={{
        backgroundColor:     groundBg,
        borderColor:         envTheme.border,
        backgroundImage:     envTheme.texture,
        backgroundPositionY: `${bgScrollY}px`,
        transition:          'background-color 1.5s ease',
        cursor:              'crosshair',
      }}
    >
      {/* ── Modifier darkness tint (ground darkens with rarity) ─────────── */}
      {tintOpacity > 0 && (
        <div className="modifier-darkness" style={{ background: `rgba(0,0,0,${tintOpacity})` }} />
      )}

      {/* ── World tints (freeze / slow-time) ────────────────────────────── */}
      {isFrozen   && <div className="world-tint world-tint--freeze" />}
      {isSlowTime && !isFrozen && <div className="world-tint world-tint--slow" />}

      {/* ── HUD ─────────────────────────────────────────────────────────── */}
      <HpBar hp={s.hp} maxHp={s.maxHp} />
      <StatsHUD score={s.score} kills={s.kills} goldThisRun={s.goldThisRun} />
      <ActiveModifierBadge modifier={s.activeModifier} />

      {/* ── Attack sweep ────────────────────────────────────────────────── */}
      <AttackSweep
        attackRenderKey={attackRenderKey}
        playerX={s.player.x}
        playerY={s.player.y}
        weaponType={s.weaponType}
        rangeMult={s.rangeMult}
        cdMult={s.cdMult}
        activeModifier={s.activeModifier}
      />

      {/* ── Spell visual effects ────────────────────────────────────────── */}
      <SpellEffectsLayer spellEffects={s.spellEffects} now={now} env={env} />

      {/* ── Ponds (below player so player walks on top) ─────────────────── */}
      <PondLayer ponds={s.ponds} env={env} activeModifierRarity={s.activeModifier?.rarity} />

      {/* ── Player ──────────────────────────────────────────────────────── */}
      <PlayerEntity
        playerX={s.player.x}
        playerY={s.player.y}
        playerFrame={s.playerFrame}
        facingAngle={s.playerFacingAngle}
        isMoving={s.playerIsMoving}
        isDashing={s.isDashing}
        isInvincible={isInvincible}
        isShielded={isShielded}
        isDodging={isDodging}
      />

      {/* ── Terrain (trees, world assets, rocks — above player) ─────────── */}
      <TerrainLayer
        trees={s.trees}
        worldAssets={s.worldAssets}
        rocks={s.rocks}
        env={env}
      />

      {/* ── Chests ──────────────────────────────────────────────────────── */}
      <ChestLayer chests={s.chests} />

      {/* ── Ground drops (XP orbs + item drops) ─────────────────────────── */}
      <GroundDropLayer groundDrops={s.groundDrops} />

      {/* ── Projectiles (from ranged enemies) ───────────────────────────── */}
      <ProjectileLayer projectiles={s.projectiles} />

      {/* ── Enemies & Bosses ────────────────────────────────────────────── */}
      <EnemyLayer
        enemies={s.enemies}
        playerCx={s.player.x + PLAYER_SIZE / 2}
        playerCy={s.player.y + PLAYER_SIZE / 2}
      />

      {/* ── Floating damage / gold numbers ──────────────────────────────── */}
      {(s.floatingTexts ?? []).map(ft => (
        <FloatingText key={ft.id} {...ft} />
      ))}

      {/* ── Tier Boss warning overlay ────────────────────────────────────── */}
      <TierBossWarning warning={s.tierBossWarning} />

      {/* ── Tier announcement ───────────────────────────────────────────── */}
      <TierAnnouncement announcement={s.tierAnnouncement} />

      {/* ── Mobile pause button (top-right, touch-device only) ──────────── */}
      <button
        data-mobile-btn="true"
        className="mobile-pause-btn"
        onClick={onMobilePause}
        aria-label="Pause"
      >⏸</button>

      {/* ── Ability bar (Q / E / W) ──────────────────────────────────────── */}
      <AbilityBar
        attackCdProg={attackCdProg}
        dashCdProg={dashCdProg}
        blockCdProg={blockCdProg}
        potionCdProg={potionCdProg}
        potionCount={s.potionCount ?? 0}
        isDashing={s.isDashing}
        onMobileAttack={onMobileAttack}
        onMobileDash={onMobileDash}
        onMobileBlock={onMobileBlock}
        onMobilePotion={onMobilePotion}
      />

      {/* ── Spell hotbar (above XP bar) ──────────────────────────────────── */}
      <SpellHotbar equippedSpells={s.runSpells ?? []} spellCooldowns={s.spellCooldowns} onCast={onMobileCast} />

      {/* ── XP bar ──────────────────────────────────────────────────────── */}
      <XpBar xp={s.xp} xpToNext={s.xpToNext} level={s.level} />

      {/* ── Overlays ────────────────────────────────────────────────────── */}
      {gameState === 'levelup'    && <LevelUpOverlay skills={levelUpSkills} onChoose={onSkillChoose} />}
      {gameState === 'magicselect' && <MagicSelectOverlay spells={magicSelectSpells ?? []} isUpgrade={magicSelectIsUpgrade ?? false} onChoose={onMagicSpellChoose} />}
      {gameState === 'modifier'   && <WorldModifierOverlay modifiers={worldModifierChoices ?? []} onChoose={onModifierChoose} />}
      {gameState === 'chestopen'  && <ChestLootModal items={chestLootOptions ?? []} onChoose={onChestLootChoose} />}
      {isPaused                   && <PauseOverlay onResume={onResume} onMenu={onMenu} onSettings={onSettings} />}
      <EnvironmentAnnouncement env={envAnnouncement} />
    </div>
  );
}
